import type { Configuration } from '@azure/msal-browser';

/**
 * Two identity planes (ADR 0001):
 *  - 'workforce' — Entra ID tenant. Admin + Room apps. Roles via app roles.
 *  - 'external'  — Entra External ID tenant. Player app. Social federation + email/OTP.
 */
export type IdentityPlane = 'workforce' | 'external';

export interface AuthEnv {
  clientId: string;
  /**
   * Workforce: tenant GUID or domain. External: the tenant **GUID** — not the subdomain, for
   * the reason `buildExternalConfig` explains. The two planes taking different kinds of value
   * under one name is the single most misconfigured setting here; see docs/entra-setup.md §3.6.
   */
  tenant: string;
  redirectUri: string;
  /** API scope, e.g. api://<api-client-id>/access_as_user */
  apiScope: string;
}

const base = (authority: string, env: AuthEnv, knownAuthorities: string[]): Configuration => ({
  auth: {
    clientId: env.clientId,
    authority,
    knownAuthorities,
    redirectUri: env.redirectUri,
    postLogoutRedirectUri: env.redirectUri,
  },
  cache: {
    // sessionStorage over localStorage: token material dies with the tab.
    cacheLocation: 'sessionStorage',
  },
});

/** Admin + Room apps. */
export function buildWorkforceConfig(env: AuthEnv): Configuration {
  return base(`https://login.microsoftonline.com/${env.tenant}`, env, []);
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Player app — Entra External ID (CIAM) authority shape.
 *
 * **`env.tenant` is the tenant GUID, and the subdomain form does not work.** It was
 * `https://<subdomain>.ciamlogin.com/<subdomain>.onmicrosoft.com` here until it met a real
 * tenant, which is the form Microsoft's own documentation shows and the one that looks right —
 * so expect somebody to try to restore it. What stops it is the issuer the tenant actually
 * publishes:
 *
 *     https://<tenant-guid>.ciamlogin.com/<tenant-guid>/v2.0
 *
 * From msal-browser 5.10.0 the discovery document's `issuer` is validated against the
 * configured authority (AzureAD/microsoft-authentication-library-for-js#8570, issue #8592), so
 * the subdomain form fails **after a successful 200** — the request is fine and the document is
 * rejected. It surfaces as `ClientAuthError: endpoints_resolution_error` from `initialize()`,
 * which names neither the value nor this file. Still unfixed in 5.20.0, and downgrading is not
 * available: `@azure/msal-react@5.5.3` peer-depends on `@azure/msal-browser@^5.17.1`.
 *
 * **This is the browser's rule, not the API's, and the two deliberately disagree.** The server's
 * `Auth:External:Authority` is still the subdomain form and is correct there, because the JWT
 * bearer handler takes `ValidIssuer` *from the document it fetched* rather than comparing it
 * against what it was configured with. Do not "align" them — see docs/entra-setup.md §3.8.
 */
export function buildExternalConfig(env: AuthEnv): Configuration {
  // Fail here, naming the value, rather than three layers down inside MSAL. This is known
  // gap 8's exact shape: VITE_* are inlined at build time, so a wrong tenant builds green,
  // deploys green and fails in somebody's browser wearing the costume of a broken app. The
  // subdomain is the wrong-but-plausible value, so it gets its own sentence.
  if (!GUID.test(env.tenant)) {
    const looksLikeSubdomain = /^[a-z0-9-]+$/i.test(env.tenant) && env.tenant.length > 0;
    throw new Error(
      `External identity plane: tenant must be the External ID tenant's GUID, not ` +
        `${env.tenant ? `"${env.tenant}"` : 'an empty value'}. ` +
        (looksLikeSubdomain
          ? `That looks like the tenant subdomain. The authority is built from the GUID for ` +
            `both host and path, because that is the issuer the tenant publishes — see the ` +
            `comment on buildExternalConfig in packages/auth/src/config.ts. `
          : '') +
        `Set VITE_ENTRA_TENANT to the Directory (tenant) ID from the tenant's Overview blade ` +
        `(docs/entra-setup.md §3.6). Note the API's Auth:External:Authority is a different ` +
        `value and stays as it is.`,
    );
  }

  const host = `${env.tenant}.ciamlogin.com`;
  return base(`https://${host}/${env.tenant}`, env, [host]);
}
